import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, KeyRound, Lock } from "lucide-react";

export default function Login() {
  const { user, login, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("password");
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);

  if (ready && user) return <Navigate to="/" replace />;

  const resetPasswordStep = () => {
    setStep("password");
    setOtp("");
    setSetup(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(
      email.trim(),
      password,
      step === "otp" ? otp.trim() : undefined,
    );
    setLoading(false);

    if (res.requires2fa) {
      setStep("otp");
      setSetup(
        res.setupRequired
          ? { secret: res.totpSecret, uri: res.otpauthUri }
          : null,
      );
      toast.message("Enter your Google Authenticator code");
      return;
    }

    if (!res.ok) {
      toast.error(res.error || "Login failed");
      return;
    }

    toast.success("Welcome back");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 flex flex-col">
      <header className="px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm bg-zinc-900 text-white flex items-center justify-center font-display text-[13px] font-bold">
            CF
          </div>
          <span className="font-display text-[15px] font-bold tracking-tight">
            Central Finance
          </span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <section className="w-full max-w-[390px]">
          <div className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
              Secure Login
            </p>
            <h1 className="font-display text-3xl font-bold tracking-tight">
              Sign in to Central Finance
            </h1>
            <p className="text-[13px] text-zinc-500 mt-3 leading-relaxed">
              Master accounts require a Google Authenticator code every time.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="space-y-5 rounded-md border hairline bg-white p-6 shadow-sm"
            data-testid="login-form"
          >
            {step === "otp" && (
              <button
                type="button"
                onClick={resetPasswordStep}
                className="inline-flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-zinc-900"
              >
                <ArrowLeft size={13} />
                Change login details
              </button>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[12.5px] text-zinc-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="h-11 bg-white"
                disabled={step === "otp"}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[12.5px] text-zinc-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="h-11 bg-white"
                disabled={step === "otp"}
              />
            </div>

            {step === "otp" && (
              <div className="space-y-4">
                {setup?.secret && (
                  <div className="rounded-md border hairline bg-zinc-50 p-3">
                    <div className="flex items-center gap-2 text-[12px] font-medium text-zinc-700">
                      <KeyRound size={14} />
                      Google Authenticator setup key
                    </div>
                    <p className="mt-2 select-all break-all font-mono text-[13px] text-zinc-950">
                      {setup.secret}
                    </p>
                    <p className="mt-2 text-[11.5px] leading-relaxed text-zinc-500">
                      Add this time-based key in Google Authenticator, then
                      enter the 6-digit code below.
                    </p>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="otp" className="text-[12.5px] text-zinc-600">
                    Authentication code
                  </Label>
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    placeholder="000000"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="h-11 bg-white font-mono text-lg tracking-[0.35em]"
                    autoFocus
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white group"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading
                ? "Checking..."
                : step === "otp"
                  ? "Verify and sign in"
                  : "Continue"}
              <ArrowRight
                size={15}
                className="ml-1.5 transition-transform group-hover:translate-x-0.5"
              />
            </Button>

            <div className="flex items-center gap-2 text-[11.5px] text-zinc-500 pt-1">
              <Lock size={12} strokeWidth={1.6} />
              <span>Protected access for finance operations</span>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}
